import { OperationVariables, useApolloClient } from '@apollo/client';
import { DocumentNode } from 'graphql';
import React from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

/**
 * Custom hook that provides cache watching functionality with initial cache read
 * This hook abstracts the pattern of:
 * 1. Reading initial data from cache
 * 2. Setting up cache.watch subscription
 * 3. Cleaning up subscription on unmount
 *
 * @param query - GraphQL DocumentNode to watch
 * @param variables - Variables for the query
 * @returns [data, setData] - Current data and setter function
 */
export function useCacheWatch<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables
>(
    query: DocumentNode,
    variables: TVariables,
): [TData | null, React.Dispatch<React.SetStateAction<TData | null>>] {
    const client = useApolloClient();
    const [data, setData] = React.useState<TData | null>(null);

    useDeepCompareEffect(() => {
        try {
            const result = client.readQuery<TData, TVariables>({
                query,
                variables,
            });
            setData(result as TData);
        } catch (error) {
            // Data not in cache yet
            setData(null);
        }
        
        // Watch for cache changes - re-establish when dependencies change
        const subscription = client.cache.watch({
            query,
            variables,
            optimistic: false,
            callback: (diff: { result?: TData }) => {
                if (diff.result) {
                    setData(diff.result);
                }
            },
        });

        return () => {
            subscription();
        };
    }, [client, query, variables]);

    return [data, setData];
}

/**
 * Custom hook specifically for Answer session cache watching
 * Provides a more specific interface for Answer-related queries
 *
 * @param query - GraphQL DocumentNode to watch
 * @param answerSessionId - Answer session ID
 * @returns [data, setData] - Current data and setter function
 */
export function useAnswerSessionCacheWatch<TData = unknown>(
    query: DocumentNode,
    answerSessionId: string,
): [TData | null, React.Dispatch<React.SetStateAction<TData | null>>] {
    return useCacheWatch<TData, { session: { sessionId: string } }>(query, {
        session: { sessionId: answerSessionId },
    });
}
